/**
 * <accessories-panel> — "Complete your setup".
 *
 * Tick an accessory, set a quantity, and it joins the main product: the theme's
 * own price switches to the running total and its Add to cart button posts the
 * lot as one cart request.
 *
 * Two things worth knowing before editing:
 *
 * 1. One tab bar serves every variant row, so counts are recomputed against
 *    whichever row is visible. The Ignite variant-picker JS toggles .hidden on
 *    the [data-variant-data-variant-id] rows; we observe that rather than
 *    listening for a variant event, because the rows are the only signal the
 *    theme reliably gives us.
 *
 * 2. The Add to cart takeover is a CAPTURE listener on `document`, not on the
 *    form. Listeners on the target element itself run in registration order
 *    regardless of the capture flag, so binding to the form couldn't guarantee
 *    running before Ignite's own submit handler. It only intervenes when
 *    something is actually ticked — with nothing selected the theme's normal
 *    path runs untouched.
 */
class AccessoriesPanel extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('.ap__tab')];
    this.summary = this.querySelector('.ap__summary');
    this.selection = new Map();
    this.currency = window.Shopify?.currency?.active || 'GBP';

    this._onInput = this._handleInput.bind(this);
    this._onClick = this._handleClick.bind(this);
    this._onSubmit = this._handleSubmit.bind(this);

    this.addEventListener('change', this._onInput);
    this.addEventListener('input', this._onInput);
    this.addEventListener('click', this._onClick);
    document.addEventListener('submit', this._onSubmit, true);

    // The main quantity is part of the total, and it lives outside this element.
    this._onMainQty = (e) => {
      if (e.target?.matches?.('[name="quantity"]')) this._render();
    };
    document.addEventListener('change', this._onMainQty);
    document.addEventListener('input', this._onMainQty);

    this._observer = new MutationObserver(() => this._sync());
    this.querySelectorAll('[data-variant-data-variant-id]').forEach((el) => {
      this._observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    });

    // Don't rely on the theme un-hiding the right row. Ignite's variant JS
    // toggles .hidden on [data-variant-data-variant-id] elements, but it has
    // been seen leaving every row hidden after a size change, which empties the
    // panel. The form's own id field is the authoritative answer to "which
    // variant is selected", so poll it and drive the rows from that. Polling
    // rather than observing because the theme sets .value as a property, which
    // fires no mutation.
    this._poll = setInterval(() => this._checkVariant(), 300);

    // The pill is positioned in pixels, so it has to be re-measured whenever the
    // tabs reflow — and a window resize is only one of the ways that happens.
    // This file's own stylesheet is loaded non-render-blocking (media="print"
    // onload), so a deferred script can run while the tabs are still unstyled;
    // observing the bar and the tabs themselves catches the moment the CSS
    // lands, a font swap changing the labels' width, and the column reflowing.
    this._onResize = () => this._positionPill();
    window.addEventListener('resize', this._onResize);

    if (window.ResizeObserver) {
      this._sizeObserver = new ResizeObserver(this._onResize);
      const bar = this.querySelector('.ap__tabs');
      if (bar) this._sizeObserver.observe(bar);
      this.tabs.forEach((tab) => this._sizeObserver.observe(tab));
    }
    document.fonts?.ready?.then(() => this._positionPill());

    // The Inc/Ex VAT switch. 'before' hands the theme's own price back while it
    // still reads in the OLD mode, so the switch can rewrite it and _render()
    // then re-stashes a base price in the mode we've just moved to. Doing both
    // off the one event would leave data-ap-base holding an ex-VAT figure to
    // restore into an inc-VAT page.
    this._onVatBefore = () => this._renderTotal(0);
    this._onVat = () => {
      this._applyVatToCards();
      this._render();
    };
    document.addEventListener('suk:vat:before', this._onVatBefore);
    document.addEventListener('suk:vat', this._onVat);
    // The switch initialises from localStorage before this element upgrades, so
    // a customer who chose ex VAT last visit has already missed the event.
    this._applyVatToCards();

    this._checkVariant();
    this._sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._onInput);
    this.removeEventListener('input', this._onInput);
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('submit', this._onSubmit, true);
    document.removeEventListener('change', this._onMainQty);
    document.removeEventListener('input', this._onMainQty);
    document.removeEventListener('suk:vat:before', this._onVatBefore);
    document.removeEventListener('suk:vat', this._onVat);
    this._observer?.disconnect();
    this._sizeObserver?.disconnect();
    window.removeEventListener('resize', this._onResize);
    clearInterval(this._poll);
    clearTimeout(this._fadeTimer);
    clearTimeout(this._settleTimer);
    // Restore the theme's price first, THEN stop watching: disconnect() also
    // discards the records our own restore just queued.
    this.selection.clear();
    this._renderTotal(0);
    this._priceObserver?.disconnect();
  }

  get _rows() {
    return [...this.querySelectorAll('.ap__row')];
  }

  /** The row for the selected variant — or null when that variant HAS no row.
   *  Never falls back to another row once the variant is known: Liquid renders
   *  at most 250 of a product's variants, so on Rubber Coolant Radiator Hose (278)
   *  the last 28 sizes have no row, and the old fallback to rows[0] showed the
   *  5mm hose's clips on a 102mm hose. */
  get _visibleRow() {
    const rows = this._rows;
    if (this._variantId) return this._rowFor(this._variantId);
    return rows.find((r) => !r.classList.contains('hidden')) || rows[0] || null;
  }

  /** The row serving a variant. One row can serve many: the snippet renders one
   *  row per distinct add-on list, so on a product whose add-ons don't vary by
   *  size every variant shares a single row. Hence "is this id in the row's
   *  list", not "is this the row's id". */
  _rowFor(id) {
    return (
      this._rows.find((r) =>
        (r.dataset.variantIds || r.dataset.variantDataVariantId || '').split(' ').includes(id)
      ) || null
    );
  }

  /** variant id -> [price, title], for the figures a shared row can't carry in
   *  its own attributes. Parsed once; falls back to the row's attributes if the
   *  map is missing (older markup) or the variant isn't in it. */
  get _variantMeta() {
    if (this._meta === undefined) {
      try {
        this._meta = JSON.parse(this.querySelector('.ap__variant-meta')?.textContent || '{}');
      } catch {
        this._meta = {};
      }
    }
    return this._meta;
  }

  /** Resolved from the form, not just this._variantId: a shared row's own
   *  price/title attributes belong to the FIRST variant in its group, so before
   *  the first _checkVariant they'd be another size's figures. */
  _metaFor() {
    return this._variantMeta[this._variantId || this._currentVariantId()];
  }

  _mainPrice(row) {
    const meta = this._metaFor();
    if (meta) return meta[0];
    return parseInt(row?.dataset.variantPrice, 10) || 0;
  }

  _mainTitle(row) {
    const meta = this._metaFor();
    const title = meta ? meta[1] : row?.dataset.variantTitle;
    return title === 'Default Title' ? '' : title || '';
  }

  _currentVariantId() {
    const field = this._form?.querySelector('[name="id"]');
    return field ? String(field.value) : null;
  }

  /** Show the row for whichever variant the form currently holds. */
  _checkVariant() {
    const id = this._currentVariantId();
    if (!id || id === this._variantId) return;
    this._variantId = id;

    const rows = this._rows;
    const match = this._rowFor(id);
    rows.forEach((r) => r.classList.toggle('hidden', r !== match));

    // No row for this size (see _visibleRow): show no panel at all rather than
    // another size's add-ons, and put the price and button back to normal.
    this.hidden = !match;
    if (!match) {
      this._clearSelection();
      this._lastRow = null;
      this._render();
      return;
    }
    this._sync();
  }

  get _form() {
    return document.querySelector('product-form form[action*="/cart/add"]');
  }

  /** A UNIT price in the mode the customer is viewing. The VAT switch skips
   *  this element's subtree precisely because the panel formats its own prices —
   *  two writers enforcing different numbers on one node would loop.
   *
   *  Converting per unit and then summing, rather than summing and converting
   *  once, is deliberate: £4.61 + £1.06 ex-VAT as a single division rounds to
   *  £4.73, while the two lines the customer can see add up to £4.72. Whatever
   *  is printed has to add up. */
  _displayMinor(minor) {
    const vat = window.SUKVat;
    return vat?.mode === 'ex' ? vat.exMinor(minor) : minor;
  }

  /** Unit price, converted then formatted. */
  _money(minor) {
    return this.formatMoney(this._displayMinor(minor));
  }

  /** A price with its "inc VAT" / "ex VAT" suffix, for the places this panel
   *  prints money itself — the VAT sweep skips this element, so the label has to
   *  come from here. Takes a figure ALREADY in display units. Empty string when
   *  the switch isn't on the page at all (non-GB markets), which leaves the bare
   *  price rather than an unexplained label. */
  _priceHtml(minorDisplay) {
    return this.formatMoney(minorDisplay) + (window.SUKVat?.tagHtml() || '');
  }

  /** Formats a figure that is ALREADY in the displayed mode. */
  formatMoney(minor) {
    const amount = minor / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: this.currency,
      }).format(amount);
    } catch (e) {
      return '£' + amount.toFixed(2);
    }
  }

  /** Re-print each card's price in the current VAT mode. Liquid renders these
   *  inc-VAT with the `money` filter, so they can't just be re-read — the pence
   *  figure on the card's own checkbox (or on the selected length, for a card
   *  with a dropdown) is the source, which also keeps rounding identical to the
   *  summary and the total. */
  _applyVatToCards() {
    this.querySelectorAll('.ap__item').forEach((item) => {
      const price = item.querySelector('.ap__item-price');
      if (!price) return;
      const chosen = item.querySelector('.ap__choice select')?.selectedOptions?.[0];
      const minor = parseInt(
        chosen?.dataset.price ?? item.querySelector('.ap__check')?.dataset.price,
        10
      );
      if (!isNaN(minor)) price.innerHTML = this._priceHtml(this._displayMinor(minor));
    });
  }

  /* ---------------- tabs ---------------- */

  /** Re-count every tab against the visible row, hide the empty ones, and make
   *  sure the active tab is one that still has something in it. */
  _sync() {
    const row = this._visibleRow;
    if (!row) return;

    if (this._lastRow && this._lastRow !== row) this._clearSelection();
    this._lastRow = row;

    let firstPopulated = null;
    let activeStillPopulated = false;
    const active = this.dataset.active;

    this.tabs.forEach((tab) => {
      const cat = tab.dataset.tab;
      const count = row.querySelectorAll(`.ap__item[data-cat="${cat}"]`).length;
      tab.querySelector('[data-count]').textContent = count;
      tab.hidden = count === 0;
      if (count > 0) {
        if (!firstPopulated) firstPopulated = cat;
        if (cat === active) activeStillPopulated = true;
      }
    });

    // Keep the customer's chosen tab across a variant change where we can; fall
    // back to the first tab that has anything rather than showing them nothing.
    this._activate(activeStillPopulated ? active : firstPopulated);

    const empty = row.querySelector('.ap__empty');
    if (empty) empty.hidden = !!firstPopulated;

    // A single remaining tab is a filter with nothing to filter.
    this.querySelector('.ap__tabs').hidden = this.tabs.filter((t) => !t.hidden).length < 2;

    this._render();
  }

  _activate(cat) {
    if (!cat) return;
    const changed = this.dataset.active !== cat;

    const markTabs = () => {
      this.tabs.forEach((tab) => {
        const on = tab.dataset.tab === cat;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this._positionPill();
    };

    const row = this._visibleRow;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!changed || !row || reduced) {
      this.dataset.active = cat;
      markTabs();
      return;
    }

    markTabs();
    this._crossfade(row, cat);
  }

  /**
   * Fade the outgoing cards away, swap, then fade the incoming ones back in
   * while the container eases between the two heights.
   *
   * The out-then-in order is the whole point: swapping display and animating
   * only the arrivals means the old cards disappear on a single frame, which
   * reads as a flicker rather than a transition. The height tween matters too —
   * tabs hold different numbers of cards, so everything below the panel jumps
   * without it.
   */
  _crossfade(row, cat) {
    clearTimeout(this._fadeTimer);
    clearTimeout(this._settleTimer);

    const fromHeight = row.getBoundingClientRect().height;
    row.classList.add('ap__row--fading');

    this._fadeTimer = setTimeout(() => {
      this.dataset.active = cat;

      // Cards are still transparent at this point, so the swap itself is unseen.
      const toHeight = row.getBoundingClientRect().height;
      row.style.overflow = 'hidden';
      row.style.height = `${fromHeight}px`;
      row.getBoundingClientRect(); // commit the start height before transitioning
      row.style.transition = 'height 280ms cubic-bezier(0.22, 0.61, 0.36, 1)';
      row.style.height = `${toHeight}px`;

      requestAnimationFrame(() => row.classList.remove('ap__row--fading'));

      this._settleTimer = setTimeout(() => {
        row.style.height = '';
        row.style.transition = '';
        row.style.overflow = '';
      }, 300);
    }, 130);
  }

  /** Slide the active pill under the current tab. */
  _positionPill() {
    const bar = this.querySelector('.ap__tabs');
    const pill = bar?.querySelector('.ap__tab-pill');
    const tab = this.tabs.find((t) => t.classList.contains('is-active') && !t.hidden);
    if (!bar || !pill || !tab) return;

    // Don't measure an unstyled bar. The stylesheet isn't script-blocking, so
    // this can run first, and the tabs are then laid out by the UA alone — the
    // icons render at the SVG default size and the bar wraps, which measured out
    // as a 60x69 pill sitting 41px BELOW the tabs, i.e. a blue block over the
    // first card. Those pixels used to stick for the life of the page. The pill
    // only being absolutely positioned once this file's CSS applies makes it the
    // readiness test; the ResizeObserver brings us straight back when it lands.
    if (getComputedStyle(pill).position !== 'absolute') return;

    const b = bar.getBoundingClientRect();
    const t = tab.getBoundingClientRect();
    if (!t.width) return;

    // First placement shouldn't slide in from the corner.
    if (!bar.classList.contains('has-pill')) pill.style.transition = 'none';

    pill.style.width = `${t.width}px`;
    pill.style.height = `${t.height}px`;
    pill.style.transform = `translate(${t.left - b.left}px, ${t.top - b.top}px)`;

    if (!bar.classList.contains('has-pill')) {
      pill.getBoundingClientRect();
      pill.style.transition = '';
      bar.classList.add('has-pill');
    }
  }

  /* ---------------- selection ---------------- */

  _handleClick(e) {
    const tab = e.target.closest('.ap__tab');
    if (tab) {
      this._activate(tab.dataset.tab);
      return;
    }

    const step = e.target.closest('.ap__qty-step');
    if (step) {
      const input = step.parentElement.querySelector('.ap__qty-input');
      const next = (parseInt(input.value, 10) || 1) + parseInt(step.dataset.step, 10);
      input.value = Math.min(99, Math.max(1, next));
      this._readItem(step.closest('.ap__item'), true);
      this._render();
      return;
    }

    // Anywhere on the card toggles it — except the bits that already do
    // something: the title and image links, and the quantity controls. A click
    // on the checkbox itself lands here as an <input> and is left alone, since
    // the browser has already toggled it and the change event handles the rest.
    const card = e.target.closest('.ap__item');
    if (card && !e.target.closest('a, button, input, select, textarea, .ap__choice')) {
      const check = card.querySelector('.ap__check');
      if (check && check.tagName === 'INPUT') {
        check.checked = !check.checked;
        this._readItem(card);
        this._render();
      }
      return;
    }

    const remove = e.target.closest('[data-remove]');
    if (remove) {
      const item = this._visibleRow?.querySelector(
        `.ap__item .ap__check[data-variant-id="${remove.dataset.remove}"]`
      );
      if (item) {
        item.checked = false;
        this._readItem(item.closest('.ap__item'));
      }
      this._render();
    }
  }

  _handleInput(e) {
    const el = e.target;
    if (el.matches('.ap__choice-select')) {
      this._changeChoice(el);
      return;
    }
    if (!el.matches('.ap__check, .ap__qty-input')) return;
    // Typing a quantity is a clear enough signal that they want the thing.
    this._readItem(el.closest('.ap__item'), el.matches('.ap__qty-input'));
    this._render();
  }

  /** A card with a dropdown (e.g. aluminium joiner: diameter preset to fit the
   *  hose, customer picks the length) re-points itself at the chosen variant.
   *  Everything downstream — selection map, summary, price, cart add — reads the
   *  checkbox's data attributes, so updating those is the whole job. Select
   *  fires both input and change; the id check makes the second a no-op. */
  _changeChoice(select) {
    const item = select.closest('.ap__item');
    const check = item?.querySelector('.ap__check');
    const opt = select.selectedOptions[0];
    if (!check || !opt || check.dataset.variantId === opt.value) return;

    this.selection.delete(check.dataset.variantId);
    check.dataset.variantId = opt.value;
    check.dataset.price = opt.dataset.price;
    const label = `${select.dataset.choice} ${opt.dataset.value}`;
    check.dataset.spec = check.dataset.specBase ? `${check.dataset.specBase} • ${label}` : label;

    const price = item.querySelector('.ap__item-price');
    if (price) {
      price.innerHTML = this._priceHtml(this._displayMinor(parseInt(opt.dataset.price, 10) || 0));
    }
    const base = select.dataset.productUrl;
    if (base) {
      const url = `${base}${base.includes('?') ? '&' : '?'}variant=${opt.value}`;
      item.querySelectorAll('a.ap__item-title, a.ap__item-media').forEach((a) => (a.href = url));
    }

    // Picking a length is as clear a signal as typing a quantity.
    this._readItem(item, true);
    this._render();
  }

  /** Push one card's current state into the selection map. */
  _readItem(item, autoTick = false) {
    if (!item) return;
    const check = item.querySelector('.ap__check');
    const qtyInput = item.querySelector('.ap__qty-input');
    if (!check || !check.dataset) return;

    if (autoTick && !check.checked) check.checked = true;

    const id = check.dataset.variantId;

    // The quantity control is inert until the card is selected — offering a
    // number to change on something you haven't chosen invites setting it and
    // wondering why nothing happened.
    item.querySelectorAll('.ap__qty-step, .ap__qty-input').forEach((el) => {
      el.disabled = !check.checked;
    });

    if (!check.checked) {
      this.selection.delete(id);
      item.classList.remove('is-selected');
      return;
    }

    const qty = Math.min(99, Math.max(1, parseInt(qtyInput?.value, 10) || 1));
    if (qtyInput) qtyInput.value = qty;
    item.classList.add('is-selected');
    this.selection.set(id, {
      id,
      qty,
      price: parseInt(check.dataset.price, 10) || 0,
      title: check.dataset.title || '',
      spec: check.dataset.spec || '',
    });
  }

  _clearSelection() {
    this.selection.clear();
    this.querySelectorAll('.ap__check').forEach((c) => {
      if (c.checked) c.checked = false;
    });
    this.querySelectorAll('.ap__qty-input').forEach((q) => {
      q.value = 1;
      q.disabled = true;
    });
    this.querySelectorAll('.ap__qty-step').forEach((b) => (b.disabled = true));
    this.querySelectorAll('.ap__item.is-selected').forEach((i) => i.classList.remove('is-selected'));
  }

  /* ---------------- rendering ---------------- */

  /** The main product's own line — always first in the summary, and not
   *  removable, since it's what the customer came for. */
  _mainLine() {
    const row = this._visibleRow;
    if (!row) return null;
    return {
      title: this.dataset.productTitle || '',
      spec: this._mainTitle(row),
      price: this._mainPrice(row),
      qty: parseInt(this._form?.querySelector('[name="quantity"]')?.value, 10) || 1,
    };
  }

  _render() {
    const items = [...this.selection.values()];
    // Every figure below is in DISPLAY units (ex-VAT when the switch is on),
    // converted per unit price so the lines and the total agree.
    const extras = items.reduce((sum, i) => sum + this._displayMinor(i.price) * i.qty, 0);
    const main = this._mainLine();
    const total = (main ? this._displayMinor(main.price) * main.qty : 0) + extras;

    if (this.summary) {
      const line = (i, removable) => `
        <li class="ap__summary-row${removable ? '' : ' ap__summary-row--main'}">
          <span class="ap__summary-qty">${i.qty}&times;</span>
          <span class="ap__summary-name">${i.title}${i.spec ? ` <span class="ap__summary-spec">${i.spec}</span>` : ''}</span>
          <span class="ap__summary-price">${this._priceHtml(this._displayMinor(i.price) * i.qty)}</span>
          ${
            removable
              ? `<button type="button" class="ap__summary-remove" data-remove="${i.id}" aria-label="Remove ${i.title}">&times;</button>`
              : '<span class="ap__summary-remove ap__summary-remove--spacer" aria-hidden="true"></span>'
          }
        </li>`;

      this.summary.querySelector('.ap__summary-list').innerHTML = [
        main ? line(main, false) : '',
        ...items.map((i) => line(i, true)),
      ].join('');
      // The summary's own total row was removed (2026-09-15); `total` still
      // drives the price and the CTA below. Guarded rather than deleted so an
      // older cached snippet that still has the row keeps working.
      const totalEl = this.summary.querySelector('[data-order-total]');
      if (totalEl) totalEl.textContent = this.formatMoney(total);
      // Ships hidden so it can't paint as an empty shell before this runs.
      this.summary.hidden = false;
    }

    this._renderTotal(extras);
    this._renderCta(total, items.length > 0);
    this._toggleDynamicCheckout(items.length > 0);
  }

  /** Say what the button will actually do: "Add setup to cart – £18.11" rather
   *  than a bare "Add to cart" that hides four other line items. */
  _renderCta(total, hasExtras) {
    const button = this._form?.querySelector('[type="submit"]');
    if (!button) return;

    const label = [...button.children].find((el) => el.tagName === 'SPAN') || button;

    // The theme re-renders this button on every variant change, so re-read the
    // original wording whenever the element itself is new — never off text we
    // may have written ourselves.
    if (this._ctaEl !== label) {
      this._ctaEl = label;
      this._ctaOriginal = label.textContent.trim();
    }

    if (!hasExtras) {
      if (this._ctaOriginal) label.textContent = this._ctaOriginal;
      return;
    }

    const wording = this.dataset.ctaLabel || 'Add setup to cart';
    // innerHTML for the VAT suffix's span; the wording is a theme setting, so it
    // goes in as text.
    const safe = document.createElement('span');
    safe.textContent = `${wording} – `;
    label.innerHTML = safe.innerHTML + this._priceHtml(total);
  }

  /** While extras are ticked, the theme's own price shows the setup total
   *  (Ashley's call, 2026-09-11 — it replaced a separate "Total with extras"
   *  line under the price). The hose's own price is still itemised in the
   *  "Added to your order" summary directly above.
   *
   *  Ignite re-renders the price element's CONTENTS on every variant change, so
   *  three rules keep this honest:
   *  - The original text is stashed on each price node the first time we write
   *    to it, and restored from there. A freshly rendered node carries no stash,
   *    so its text is the theme's — never read a "base" off text we wrote.
   *  - An observer re-applies the total if the theme re-renders while extras are
   *    still ticked. It checks the variant first, so a size change clears the
   *    selection instead of briefly painting the old setup total on the new size.
   *  - Only text is ever written, and only when it differs, so the observer's
   *    own writes settle rather than loop. */
  _renderTotal(extras) {
    const priceEl = document.querySelector('[id^="price-"]');
    if (!priceEl) return;
    this._watchPrice(priceEl);

    const price = priceEl.querySelector('.price');
    const targets = priceEl.querySelectorAll(
      '.price__regular .price-item--regular, .price__sale .price-item--sale'
    );

    // The stash and the comparisons are on innerHTML, not text: the price the
    // theme rendered may already carry the VAT switch's "inc VAT" tag, and
    // restoring text alone would strip it and leave the total unlabelled.
    if (!extras) {
      targets.forEach((el) => {
        if (el.dataset.apBase === undefined) return;
        if (el.innerHTML !== el.dataset.apBase) el.innerHTML = el.dataset.apBase;
        delete el.dataset.apBase;
      });
      price?.classList.remove('ap-price--with-extras');
      return;
    }

    const base = this._mainPrice(this._visibleRow);
    const mainQty = parseInt(this._form?.querySelector('[name="quantity"]')?.value, 10) || 1;
    const html = this._priceHtml(this._displayMinor(base) * mainQty + extras);

    targets.forEach((el) => {
      if (el.dataset.apBase === undefined) el.dataset.apBase = el.innerHTML;
      if (el.innerHTML !== html) el.innerHTML = html;
    });
    // Hides a sale's struck-through compare-at price and the sale colour — a
    // "was £9" beside a setup total would read as a discount on the setup.
    price?.classList.add('ap-price--with-extras');
  }

  _watchPrice(priceEl) {
    if (this._priceWatched === priceEl) return;
    this._priceObserver?.disconnect();
    this._priceWatched = priceEl;
    this._priceObserver = new MutationObserver(() => {
      this._checkVariant();
      if (!this.selection.size) return;
      const extras = [...this.selection.values()].reduce(
        (sum, i) => sum + this._displayMinor(i.price) * i.qty,
        0
      );
      this._renderTotal(extras);
    });
    this._priceObserver.observe(priceEl, { childList: true, subtree: true, characterData: true });
  }

  /** Shop Pay and friends go straight to checkout with only the form's own
   *  line, so the extras would be silently dropped. Hide them while anything is
   *  ticked rather than letting an order lose half of what was chosen.
   *  (No explanatory note under the button — Ashley removed it 2026-09-11; the
   *  button's own "Add setup to cart – £x" wording carries the message.) */
  _toggleDynamicCheckout(hasExtras) {
    const dynamic = this._form?.querySelector('.shopify-payment-button');
    if (!dynamic) return;
    dynamic.hidden = hasExtras;
  }

  /* ---------------- add to cart ---------------- */

  async _handleSubmit(e) {
    const form = e.target;
    if (form !== this._form || this.selection.size === 0) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const button = form.querySelector('[type="submit"]');
    const mainId = form.querySelector('[name="id"]')?.value;
    const mainQty = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;
    if (!mainId) return;

    const items = [
      { id: Number(mainId), quantity: mainQty },
      ...[...this.selection.values()].map((i) => ({ id: Number(i.id), quantity: i.qty })),
    ];

    button?.setAttribute('aria-disabled', 'true');
    button?.classList.add('loading');

    try {
      const drawer = document.querySelector('cart-drawer');
      const notification = document.querySelector('cart-notification');
      const cartItems = document.querySelector('cart-items');

      let sections = [];
      if (drawer?.getSectionsToRender) sections = drawer.getSectionsToRender().map((s) => s.id);
      if (notification?.getSectionsToRender) {
        window.loadTemplateContent?.(notification);
        sections = [...sections, ...notification.getSectionsToRender().map((s) => s.id)];
      }
      if (cartItems?.getSectionsToRender) {
        sections = [...sections, ...cartItems.getSectionsToRender().map((s) => s.section)];
      }

      const action = window.theme?.settings?.cartDrawer?.atcAction;
      if (action === 'drawer' && drawer?.setActiveElement) drawer.setActiveElement(button);
      else if (action === 'notification' && notification?.setActiveElement) {
        notification.setActiveElement(button);
      }

      const res = await fetch(window.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: [...new Set(sections)],
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();

      if (data.status) {
        this._showError(form, data.description || data.message);
        return;
      }

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'accessories-panel',
          productVariantId: String(mainId),
          cartData: data,
        });
      }
      drawer?.renderContents?.(data);
      cartItems?.renderContents?.(data);
      notification?.renderContents?.(data);

      this._clearSelection();
      this._render();
    } catch (err) {
      console.error('accessories-panel: add to cart failed', err);
      this._showError(form, 'Sorry — that could not be added. Please try again.');
    } finally {
      button?.removeAttribute('aria-disabled');
      button?.classList.remove('loading');
      // Mirrors product-form.js's own finally block. The drawer's open() runs on
      // transitionend and trap-focuses `.drawer__inner-empty` while the drawer
      // ELEMENT still carries is-empty — its renderContents only clears the flag
      // on `.drawer__inner` — so a first add to an empty cart threw
      // "Cannot read properties of null (reading 'querySelectorAll')".
      document.querySelectorAll('cart-drawer.is-empty, cart-notification.is-empty').forEach((el) => {
        el.classList.remove('is-empty');
      });
    }
  }

  _showError(form, message) {
    const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    const target = wrapper?.querySelector('.product-form__error-message');
    if (!wrapper || !target) {
      alert(message);
      return;
    }
    target.textContent = message;
    wrapper.hidden = false;
  }
}

if (!customElements.get('accessories-panel')) {
  customElements.define('accessories-panel', AccessoriesPanel);
}
